import React, { useCallback, useState, useEffect, Suspense } from 'react'
import CsvDownloader from 'react-csv-downloader'
import { observer } from 'mobx-react'
import { computed } from 'mobx'
import moment from 'moment'
import makeStyles from '@mui/styles/makeStyles'
import { Box, Button, IconButton, Tab, Tabs } from '@mui/material'

import { APP_PAYMENT_METHOD } from 'admin/config'
import { STATUS_TYPE } from 'admin/config/status-type'
import * as srv from 'admin/services'
import { formatDate } from 'admin/utils/helper'
import DateRangePicker from 'admin/components/date-range-picker'

import DateRangeIcon from '@mui/icons-material/DateRange'
import FilterListIcon from '@mui/icons-material/FilterList'

import { useStores, useApiRequest } from 'admin/hooks'
import Page from 'admin/components/page'

import InvoiceInfo from './info'
import InvoiceDataGrid from './datagrid'
import styles from './styles'
import FilterInsideDialog from './filter'
import SyncButton from './sync'

const useStyles = makeStyles(styles)

const columns = [
  {
    id: 'invoiceNumber',
    displayName: 'Invoice Number',
  },
  {
    id: 'invoiceDate',
    displayName: 'Invoice Date',
  },
  {
    id: 'customerName',
    displayName: 'Customer',
  },
  {
    id: 'subtotal',
    displayName: 'Subtotal',
  },
  {
    id: 'paymentMethod',
    displayName: 'Payment Method',
  },
  {
    id: 'paymentReference',
    displayName: 'Payment Reference',
  },
  {
    id: 'paymentStatus',
    displayName: 'Payment Status',
  },
  {
    id: 'deliveryDate',
    displayName: 'Delivery Date',
  },
  {
    id: 'driverName',
    displayName: 'Driver Name',
  },
]

const sanitizeCsvValue = (value) => String(value ?? '-').replaceAll(',', '')

const FILENAME = `DMS_Invoice_Report_${moment().format('DDMMYY')}`

const FilterOutsideDialog = observer(() => {
  const classes = useStyles()
  const { invoiceStore } = useStores()
  const [anchorEl, setAnchorEl] = useState(null)
  const { isDateRangeActive, datePickerSelectionRange } = invoiceStore

  const filterLength = computed(() => {
    const object = {
      prestoId: invoiceStore.query.prestoId,
      paymentStatus: invoiceStore.query.paymentStatus,
      customerId: invoiceStore.query.customerId,
      driverId: invoiceStore.query.driverId,
      paymentMethod: invoiceStore.query.paymentMethod,
    }
    const objArr = Object.values(object)
    const filterArr = objArr.filter((item) => item)
    return filterArr.length
  }).get()

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = useCallback((value) => {
    const { startDate, endDate } = value.selection
    invoiceStore.updateDatePickerSelectionRange(startDate, endDate)
  }, [])

  const renderIcon = () => {
    return (
      <>
        {filterLength > 0 ? (
          <div className={classes.filterNumber}>{filterLength}</div>
        ) : (
          <FilterListIcon />
        )}
        <span>Filter</span>
      </>
    )
  }

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1rem',
      }}
    >
      <div className={classes.filterWrapper}>
        <div className={classes.searchField}>
          <Button
            onClick={() => {
              invoiceStore.setOpenDialog(null, 'filter')
            }}
            variant="outlined"
            color="secondary"
          >
            {renderIcon()}
          </Button>
        </div>
        {isDateRangeActive && (
          <>
            <div className={classes.datePickerStartEnd}>
              <IconButton
                onClick={handleClick}
                size="large"
              >
                <DateRangeIcon />
              </IconButton>
              <span>
                {formatDate(datePickerSelectionRange.startDate)} -{' '}
                {formatDate(datePickerSelectionRange.endDate)}
              </span>
            </div>
            <DateRangePicker
              anchorEl={anchorEl}
              handleClose={handleClose}
              handleSelect={handleSelect}
              selectionRange={invoiceStore.datePickerSelectionRange}
            />
          </>
        )}
        <SyncButton className={classes.syncButton} />
      </div>
    </Box>
  )
})

const Invoice = observer(() => {
  const classes = useStyles()
  const [activeTab, setActiveTab] = useState(0)
  const { invoiceStore } = useStores()

  const { request: exportInvoice, isLoading: isExporting } = useApiRequest(srv.fetchInvoice, {
    blocking: true,
  })

  useEffect(() => {
    let params = {}
    if (activeTab === 0) params = { defaultDriver: true }
    invoiceStore.fetch(params)
  }, [invoiceStore.queryParams, activeTab])

  useEffect(() => {
    return () => {
      invoiceStore.resetQuery()
    }
  }, [])

  const handleChangeTab = useCallback((event, value) => {
    setActiveTab(value)
  }, [])

  const getDataInvoice = async () => {
    const params = {
      isExportCsv: true,
      sortBy: 'updatedAt',
      sortDirection: 'desc',
      limit: invoiceStore.rowsPerPage,
      offset: invoiceStore.currentPage * invoiceStore.rowsPerPage,
    }
    if (activeTab === 0) params.defaultDriver = true
    // params query based on what is selected from the invoice filter
    if (invoiceStore.isDateRangeActive) {
      const sDate = moment(invoiceStore.datePickerSelectionRange.startDate)
        .startOf('day')
        .toISOString()
      const eDate = moment(invoiceStore.datePickerSelectionRange.endDate).endOf('day').toISOString()
      params.startDate = sDate
      params.endDate = eDate
    }
    if (invoiceStore.query.prestoId) params.prestoId = invoiceStore.query.prestoId
    if (invoiceStore.query.paymentStatus) params.paymentStatus = invoiceStore.query.paymentStatus
    if (invoiceStore.query.customerId) params.customerId = invoiceStore.query.customerId
    if (invoiceStore.query.driverId) params.driverId = invoiceStore.query.driverId
    if (invoiceStore.query.paymentMethod) params.paymentMethod = invoiceStore.query.paymentMethod

    const response = await exportInvoice(params)

    return response.data.list.map((invoice) => {
      const paymentReference =
        invoice.payments
          ?.map((payment) => {
            return `${formatDate(payment.paymentTime)} | ${payment.referenceNumber} | $${Number(payment.amount).toFixed(2)}`
          })
          .join(', ') || '-'

      // status that allows delivery info to appear
      const paymentStatusLower = ['paid', 'received', 'excess'].includes(
        (invoice.paymentStatus || '').toLowerCase(),
      )
      const orderStatusLower = (
        invoice.orderStatus ||
        invoice.originalResponse?.statusTypes?.name ||
        ''
      ).toLowerCase()
      const isDeliveryVisible = orderStatusLower === STATUS_TYPE.COMPLETED || paymentStatusLower

      return {
        invoiceNumber: sanitizeCsvValue(invoice.prestoId),
        invoiceDate: sanitizeCsvValue(formatDate(invoice.originalResponse?.droppedOffTime)),
        customerName: sanitizeCsvValue(
          invoice.customer?.name || invoice.customer?.originalData.name || '-',
        ),
        subtotal: sanitizeCsvValue(
          Number(invoice.amount).toFixed(2) ||
            Number(invoice.originalResponse?.revenue).toFixed(2) ||
            0,
        ),
        paymentMethod: sanitizeCsvValue(
          invoice?.paymentMethod === APP_PAYMENT_METHOD.value
            ? APP_PAYMENT_METHOD.display
            : invoice?.paymentMethod || '-',
        ),
        paymentReference: sanitizeCsvValue(paymentReference),
        paymentStatus: sanitizeCsvValue(invoice.paymentStatus || '-'),
        deliveryDate: sanitizeCsvValue(
          isDeliveryVisible ? formatDate(invoice.originalResponse?.readyTime) : '-',
        ),
        driverName: sanitizeCsvValue(
          isDeliveryVisible ? invoice.booking?.driver?.name || '-' : '-',
        ),
      }
    })
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Page>
        <FilterOutsideDialog />
        <FilterInsideDialog />
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            textColor="secondary"
            indicatorColor="secondary"
            classes={{ indicator: classes.indicator }}
          >
            {['Driver Invoices', 'All Invoices'].map((tab) => (
              <Tab
                key={tab}
                label={tab}
                classes={{
                  root: classes.tab,
                }}
              />
            ))}
          </Tabs>
          <Box className={classes.actionExport}>
            <CsvDownloader
              filename={FILENAME}
              extension=".csv"
              separator=","
              wrapColumnChar=""
              columns={columns}
              datas={getDataInvoice}
              text="EXPORT"
            >
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                className={classes.buttonExport}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting..' : 'Export'}
              </Button>
            </CsvDownloader>
          </Box>
        </Box>
        <InvoiceDataGrid />
        <InvoiceInfo />
      </Page>
    </Suspense>
  )
})

export default Invoice
